import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import { theme } from '../../../styles/theme';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { mergeStyles, styles } from '../../../styles/utils';
import connieConcierge from '../../../assets/connie-concierge-no-bckgn.png';
import type { FormData } from '../../../types';
import { CtaButton } from './shared';

interface Props { form: FormData; onChange: (f: keyof FormData, v: unknown) => void; onNext: () => void; onBack?: () => void; }

export function StepRequirements({ form, onChange, onNext, onBack }: Props) {
  const mobile = useIsMobile();
  const f = mobile ? theme.form.mobile : theme.form;
  return (
    <div style={{ maxWidth: mobile ? '100%' : 'min(80%, 700px)', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.md, marginBottom: mobile ? theme.spacing['2xl'] : theme.spacing['4xl'] }}>
        <img src={connieConcierge} alt="Connie" referrerPolicy="no-referrer"
          style={{ width: 'auto', height: mobile ? 'min(12vh, 100px)' : 'min(20vh, 250px)', objectFit: 'contain' }}
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
        <div>
          <h2 style={{ fontFamily: theme.fontFamily.display, fontSize: f.headingSize, fontWeight: theme.fontWeight.light, color: theme.colors.navy, margin: 0, letterSpacing: '-0.02em', textAlign: 'center' }}>
            "Any special{' '}
            <span style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal }}>requests?</span>"
          </h2>
          <p style={{ color: theme.colors.textMuted, fontSize: f.subtitleSize, fontWeight: theme.fontWeight.light, margin: `${theme.spacing.xs} 0 0`, textAlign: 'center' }}>
            Your privacy is paramount. I use <strong style={{ color: theme.colors.teal }}>Bedrock Guardrails</strong> to ensure your data stays safe while I work my magic.
          </p>
        </div>
      </div>

      <TextareaFocused form={form} onChange={onChange} />

      <CtaButton
        label="Curate my journey"
        icon={<motion.div animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}><Sparkles size={16} /></motion.div>}
        onClick={onNext}
        onBack={onBack}
        alwaysActive
      />
    </div>
  );
}

function TextareaFocused({ form, onChange }: { form: FormData; onChange: (f: keyof FormData, v: unknown) => void }) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      value={form.requirements}
      onChange={e => onChange('requirements', e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder="Wheelchair access, nut allergy, ground floor rooms only … or any extra magic you'd like me to consider?"
      rows={5}
      style={mergeStyles(styles.input, {
        width: '100%', resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.6',
        borderColor: focused ? theme.colors.teal : undefined,
        transition: `border-color ${theme.transitions.fast}`,
      })}
      autoFocus
    />
  );
}
